import { PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor';
import { useCallback, useEffect, useState } from 'react';
import { getPurchaseStrategy, initializeRevenueCat } from '../config/revenuecat/index';
import type { CrossPlatformCustomerInfo, CrossPlatformOffering, CrossPlatformPackage, IPurchaseStrategy } from '../config/revenuecat/types';
import { useAuth } from './Auth';
import { useAnalytics } from './useAnalytics';

// Re-export types for backward compatibility
export type { CrossPlatformCustomerInfo, CrossPlatformOffering, CrossPlatformPackage } from '../config/revenuecat/types';

export interface UseRevenueCatReturn {
  customerInfo: CrossPlatformCustomerInfo | null;
  currentOffering: CrossPlatformOffering | null;
  isLoading: boolean;
  error: UseRevenueCatErrors;
  setError: (error: UseRevenueCatErrors) => void;
  purchasePackage: (packageToPurchase: CrossPlatformPackage) => Promise<void>;
  restorePurchases: () => Promise<void>;
  refreshCustomerInfo: () => Promise<CrossPlatformCustomerInfo | null>;
  setCustomerAttributes: (attributes: Record<string, string>) => Promise<void>;
}

export interface UseRevenueCatErrors {
  customerInfo?: string | null;
  offerings?: string | null;
  purchase?: string | null;
  restore?: string | null;
}

export const useRevenueCat = (): UseRevenueCatReturn => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CrossPlatformCustomerInfo | null>(null);
  const [currentOffering, setCurrentOffering] = useState<CrossPlatformOffering | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<UseRevenueCatErrors>({
    customerInfo: null,
    offerings: null,
    purchase: null,
    restore: null,
  });
  const { refreshAuthToken, user, isAnonymous } = useAuth();
  const { logEvent } = useAnalytics();

  // Get the purchase strategy instance
  const getStrategy = useCallback((): IPurchaseStrategy => {
    return getPurchaseStrategy();
  }, [isInitialized]);

  // Function to wait for RevenueCat claims to be updated in Firebase Auth token
  const waitForClaimsUpdate = useCallback(
    async (timeoutMs: number = 10000): Promise<boolean> => {
      const startTime = Date.now();
      const pollInterval = 1000; // Check every 1 second

      while (Date.now() - startTime < timeoutMs) {
        try {
          // Refresh the auth token and check if user is now Pro
          const isPro = await refreshAuthToken();

          if (isPro) {
            return true;
          }

          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        } catch (error) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
      }

      return false;
    },
    [refreshAuthToken]
  );

  // Fetch customer info
  const refreshCustomerInfo = useCallback(async (): Promise<CrossPlatformCustomerInfo | null> => {
    try {
      setError((prev) => ({ ...prev, customerInfo: null }));
      const strategy = getStrategy();
      const customerInfo = await strategy.getCustomerInfo();
      setCustomerInfo(customerInfo);
      return customerInfo;
    } catch (err: any) {
      // Handle specific RevenueCat error cases for customer info
      switch (err.code) {
        case PURCHASES_ERROR_CODE.NETWORK_ERROR:
          setError((prev) => ({ ...prev, customerInfo: 'Network error. Please check your internet connection and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.OFFLINE_CONNECTION_ERROR:
          setError((prev) => ({ ...prev, customerInfo: 'No internet connection. Please connect to the internet and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.INVALID_CREDENTIALS_ERROR:
          setError((prev) => ({ ...prev, customerInfo: 'Configuration error. Please restart the app and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.UNKNOWN_BACKEND_ERROR:
        case PURCHASES_ERROR_CODE.UNEXPECTED_BACKEND_RESPONSE_ERROR:
          setError((prev) => ({ ...prev, customerInfo: 'Server error. Please try again later.' }));
          break;

        case PURCHASES_ERROR_CODE.INVALID_APP_USER_ID_ERROR:
          setError((prev) => ({ ...prev, customerInfo: 'Account configuration error. Please restart the app and try again.' }));
          break;

        default:
          setError((prev) => ({ ...prev, customerInfo: 'Failed to fetch customer information. Please try again.' }));
          break;
      }
      return null;
    }
  }, [getStrategy]);

  // Fetch current offering (paywall)
  const fetchOfferings = useCallback(async () => {
    try {
      setError((prev) => ({ ...prev, offerings: null }));
      const strategy = getStrategy();
      const offerings = await strategy.getOfferings();

      if (offerings.current) {
        setCurrentOffering(offerings.current);
      }
    } catch (err: any) {
      // Handle specific RevenueCat error cases for offerings
      switch (err.code) {
        case PURCHASES_ERROR_CODE.NETWORK_ERROR:
          setError((prev) => ({ ...prev, offerings: 'Network error. Please check your internet connection and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.OFFLINE_CONNECTION_ERROR:
          setError((prev) => ({ ...prev, offerings: 'No internet connection. Please connect to the internet and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.INVALID_CREDENTIALS_ERROR:
          setError((prev) => ({ ...prev, offerings: 'Configuration error. Please restart the app and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.UNKNOWN_BACKEND_ERROR:
        case PURCHASES_ERROR_CODE.UNEXPECTED_BACKEND_RESPONSE_ERROR:
          setError((prev) => ({ ...prev, offerings: 'Server error. Please try again later.' }));
          break;

        default:
          setError((prev) => ({ ...prev, offerings: 'Failed to fetch subscription plans. Please try again.' }));
          break;
      }
    }
  }, [getStrategy]);

  // Purchase a package
  const purchasePackage = useCallback(
    async (packageToPurchase: CrossPlatformPackage) => {
      try {
        setError((prev) => ({ ...prev, purchase: null }));
        setIsLoading(true);

        const strategy = getStrategy();
        const result = await strategy.purchasePackage(packageToPurchase);

        setCustomerInfo(result.customerInfo);

        // Wait for the claims to be properly updated
        const claimsUpdated = await waitForClaimsUpdate();

        if (claimsUpdated) {
          logEvent('claims_updated_successfully', { source: 'purchase' });
        } else {
          logEvent('claims_update_failed', { source: 'purchase' });
          setError((prev) => ({
            ...prev,
            purchase:
              'There was an issue with detecting your purchase. Please try restoring purchases from the settings menu, under Subscription details.',
          }));
        }
      } catch (err: any) {
        logEvent('purchase_failed', { error: err.code });
        // Handle specific RevenueCat error cases based on documentation
        switch (err.code) {
          case PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Purchase was cancelled' }));
            break;

          case PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Payment is pending. Please complete the verification process or wait for confirmation.' }));
            break;

          case PURCHASES_ERROR_CODE.PRODUCT_ALREADY_PURCHASED_ERROR:
            setError((prev) => ({
              ...prev,
              purchase: 'You already own this subscription. Please try restoring purchases from the settings menu, under Subscription details.',
            }));
            break;

          case PURCHASES_ERROR_CODE.PRODUCT_NOT_AVAILABLE_FOR_PURCHASE_ERROR:
            setError((prev) => ({ ...prev, purchase: 'This subscription is not available for purchase. Please try again later.' }));
            break;

          case PURCHASES_ERROR_CODE.PURCHASE_NOT_ALLOWED_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Purchases are not allowed on this device. Please check your device settings.' }));
            break;

          case PURCHASES_ERROR_CODE.PURCHASE_INVALID_ERROR:
            setError((prev) => ({
              ...prev,
              purchase: 'There was an issue with your payment method. Please check your payment details and try again.',
            }));
            break;

          case PURCHASES_ERROR_CODE.INSUFFICIENT_PERMISSIONS_ERROR:
            setError((prev) => ({
              ...prev,
              purchase: 'Your device does not have permission to make purchases. Please check your account settings.',
            }));
            break;

          case PURCHASES_ERROR_CODE.INELIGIBLE_ERROR:
            setError((prev) => ({ ...prev, purchase: 'You are not eligible for this offer. Please check the subscription terms.' }));
            break;

          case PURCHASES_ERROR_CODE.NETWORK_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Network error. Please check your internet connection and try again.' }));
            break;

          case PURCHASES_ERROR_CODE.OFFLINE_CONNECTION_ERROR:
            setError((prev) => ({ ...prev, purchase: 'No internet connection. Please connect to the internet and try again.' }));
            break;

          case PURCHASES_ERROR_CODE.STORE_PROBLEM_ERROR:
            setError((prev) => ({ ...prev, purchase: 'There is a problem connecting to the app store. Please try again later.' }));
            break;

          case PURCHASES_ERROR_CODE.INVALID_CREDENTIALS_ERROR:
            setError((prev) => ({ ...prev, purchase: 'There is a configuration issue. Please contact support.' }));
            break;

          case PURCHASES_ERROR_CODE.INVALID_RECEIPT_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Invalid purchase receipt. Please try again or contact support.' }));
            break;

          case PURCHASES_ERROR_CODE.RECEIPT_ALREADY_IN_USE_ERROR:
            setError((prev) => ({
              ...prev,
              purchase: 'This purchase is already linked to another account. Please restore purchases or contact support.',
            }));
            break;

          case PURCHASES_ERROR_CODE.OPERATION_ALREADY_IN_PROGRESS_ERROR:
            setError((prev) => ({ ...prev, purchase: 'A purchase is already in progress. Please wait for it to complete.' }));
            break;

          case PURCHASES_ERROR_CODE.INVALID_APP_USER_ID_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Account configuration error. Please restart the app and try again.' }));
            break;

          case PURCHASES_ERROR_CODE.UNKNOWN_BACKEND_ERROR:
          case PURCHASES_ERROR_CODE.UNEXPECTED_BACKEND_RESPONSE_ERROR:
            setError((prev) => ({ ...prev, purchase: 'Server error. Please try again later or contact support if the problem persists.' }));
            break;

          default:
            setError((prev) => ({ ...prev, purchase: 'Failed to complete purchase. Please try again or contact support if the problem persists.' }));
            break;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getStrategy, waitForClaimsUpdate]
  );

  // Restore purchases (if supported by the strategy)
  const restorePurchases = useCallback(async () => {
    try {
      setError((prev) => ({ ...prev, restore: null }));
      setIsLoading(true);

      const strategy = getStrategy();

      if (strategy.restorePurchases) {
        const restoredInfo = await strategy.restorePurchases();
        setCustomerInfo(restoredInfo);

        // Wait for the claims to be properly updated
        const claimsUpdated = await waitForClaimsUpdate();

        if (claimsUpdated) {
          logEvent('claims_updated_successfully', { source: 'restore' });
        } else {
          logEvent('claims_update_failed', { source: 'restore' });
          setError((prev) => ({ ...prev, restore: 'There was an issue with detecting your purchase. Please try again later.' }));
        }
      } else {
        setError((prev) => ({ ...prev, restore: 'Restore purchases not supported on this platform' }));
      }
    } catch (err: any) {
      logEvent('restore_failed', { error: err.code });
      // Handle specific RevenueCat error cases for restore purchases
      switch (err.code) {
        case PURCHASES_ERROR_CODE.INVALID_RECEIPT_ERROR:
          setError((prev) => ({ ...prev, restore: 'Invalid receipt found. No purchases to restore.' }));
          break;

        case PURCHASES_ERROR_CODE.MISSING_RECEIPT_FILE_ERROR:
          setError((prev) => ({ ...prev, restore: 'No receipt file found. Make sure you have made purchases with this Apple account.' }));
          break;

        case PURCHASES_ERROR_CODE.NETWORK_ERROR:
          setError((prev) => ({ ...prev, restore: 'Network error. Please check your internet connection and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.OFFLINE_CONNECTION_ERROR:
          setError((prev) => ({ ...prev, restore: 'No internet connection. Please connect to the internet and try again.' }));
          break;

        case PURCHASES_ERROR_CODE.STORE_PROBLEM_ERROR:
          setError((prev) => ({ ...prev, restore: 'There is a problem connecting to the app store. Please try again later.' }));
          break;

        case PURCHASES_ERROR_CODE.INVALID_CREDENTIALS_ERROR:
          setError((prev) => ({ ...prev, restore: 'Configuration error. Please contact support.' }));
          break;

        case PURCHASES_ERROR_CODE.UNKNOWN_BACKEND_ERROR:
        case PURCHASES_ERROR_CODE.UNEXPECTED_BACKEND_RESPONSE_ERROR:
          setError((prev) => ({ ...prev, restore: 'Server error. Please try again later or contact support if the problem persists.' }));
          break;

        default:
          setError((prev) => ({ ...prev, restore: 'Failed to restore purchases. Please try again or contact support if the problem persists.' }));
          break;
      }
    } finally {
      setIsLoading(false);
    }
  }, [getStrategy, waitForClaimsUpdate]);

  // Set customer attributes
  const setCustomerAttributes = useCallback(
    async (attributes: Record<string, string>) => {
      setError((prev) => ({ ...prev, setCustomerAttributes: null }));
      setIsLoading(true);

      const strategy = getStrategy();
      try {
        await strategy.setCustomerAttributes(attributes);
      } catch (err: any) {
        logEvent('set_customer_attributes_failed', { error: err.code });
        setError((prev) => ({ ...prev, setCustomerAttributes: 'Failed to set customer attributes. Please try again.' }));
      } finally {
        setIsLoading(false);
      }
    },
    [getStrategy]
  );

  useEffect(() => {
    const initRevenueCat = async () => {
      if (!user) return;
      if (user.isAnonymous) {
        return;
      }
      await initializeRevenueCat(user.uid, user.email ?? undefined);
      setIsInitialized(true);
    };
    initRevenueCat();
  }, [user, user?.isAnonymous]);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([refreshCustomerInfo(), fetchOfferings()]);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [refreshCustomerInfo, fetchOfferings]);

  return {
    customerInfo,
    currentOffering,
    isLoading,
    error,
    setError,
    purchasePackage,
    restorePurchases,
    refreshCustomerInfo,
    setCustomerAttributes,
  };
};
